import { createPaletteChannel, varAlpha } from './styles/index.js';
import COLORS from './colors.json';

// ----------------------------------------------------------------------

interface PaletteColor {
  [key: string]: string;
  [key: `${string}Channel`]: string;
}

interface TextPalette extends PaletteColor {
  primary: string;
  secondary: string;
  disabled: string;
}

interface BackgroundPalette extends PaletteColor {
  paper: string;
  default: string;
  neutral: string;
}

interface BaseAction {
  hover: string;
  selected: string;
  focus: string;
  disabled: string;
  disabledBackground: string;
  hoverOpacity: number;
  disabledOpacity: number;
}

interface ActionPalette extends BaseAction {
  active: string;
}

interface BasePalette {
  primary: PaletteColor;
  secondary: PaletteColor;
  info: PaletteColor;
  success: PaletteColor;
  warning: PaletteColor;
  error: PaletteColor;
  grey: PaletteColor;
  common: PaletteColor;
  divider: string;
  action: {
    light: ActionPalette;
    dark: ActionPalette;
  };
}

interface ThemePalette extends Omit<BasePalette, 'action'> {
  text: TextPalette;
  background: BackgroundPalette;
  action: ActionPalette;
}

interface ColorScheme {
  palette: ThemePalette;
}

// Grey
export const grey: PaletteColor = createPaletteChannel(COLORS.grey);

// Primary
export const primary: PaletteColor = createPaletteChannel(COLORS.primary);

// Secondary
export const secondary: PaletteColor = createPaletteChannel(COLORS.secondary);

// Info
export const info: PaletteColor = createPaletteChannel(COLORS.info);

// Success
export const success: PaletteColor = createPaletteChannel(COLORS.success);

// Warning
export const warning: PaletteColor = createPaletteChannel(COLORS.warning);

// Error
export const error: PaletteColor = createPaletteChannel(COLORS.error);

// Common
export const common: PaletteColor = createPaletteChannel(COLORS.common);

// Text
export const text: {
  light: TextPalette;
  dark: TextPalette;
} = {
  light: createPaletteChannel({
    primary: grey[800],
    secondary: grey[600],
    disabled: grey[500],
  }) as TextPalette,
  dark: createPaletteChannel({
    primary: '#FFFFFF',
    secondary: grey[500],
    disabled: grey[600],
  }) as TextPalette,
};

// Background
export const background: {
  light: BackgroundPalette;
  dark: BackgroundPalette;
} = {
  light: createPaletteChannel({
    paper: '#FFFFFF',
    default: '#FFFFFF',
    neutral: grey[200],
  }) as BackgroundPalette,
  dark: createPaletteChannel({
    paper: grey[800],
    default: grey[900],
    neutral: '#28323D',
  }) as BackgroundPalette,
};

// Action
export const baseAction: BaseAction = {
  hover: varAlpha(grey['500Channel'], 0.08),
  selected: varAlpha(grey['500Channel'], 0.16),
  focus: varAlpha(grey['500Channel'], 0.24),
  disabled: varAlpha(grey['500Channel'], 0.8),
  disabledBackground: varAlpha(grey['500Channel'], 0.24),
  hoverOpacity: 0.08,
  disabledOpacity: 0.48,
};

export const action: {
  light: ActionPalette;
  dark: ActionPalette;
} = {
  light: { ...baseAction, active: grey[600] },
  dark: { ...baseAction, active: grey[500] },
};

/*
 * Base palette
 */
export const basePalette: BasePalette = {
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  grey,
  common,
  divider: varAlpha(grey['500Channel'], 0.2),
  action,
};

export const lightPalette: ThemePalette = {
  ...basePalette,
  text: text.light,
  background: background.light,
  action: action.light,
};

export const darkPalette: ThemePalette = {
  ...basePalette,
  text: text.dark,
  background: background.dark,
  action: action.dark,
};

// ----------------------------------------------------------------------

export const colorSchemes: {
  light: ColorScheme;
  dark: ColorScheme;
} = {
  light: { palette: lightPalette },
  dark: { palette: darkPalette },
};
